import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { saveAs } from 'file-saver';
import { Subscription } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { finalize, skip } from 'rxjs/operators';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { ClaimService } from 'src/app/services/claim.service';
import { CustomerService } from 'src/app/services/customer.service';
import { ClaimPdfExportService } from '../claim-pdf-export.service';

type StatusCardKey =
  | 'all'
  | 'draft'
  | 'inProgress'
  | 'accepted'
  | 'completed'
  | 'cancel'
  | 'rejected';

interface StatusCard {
  key: StatusCardKey;
  label: string;
  count: number;
  icon: string;
  color: string;
}

interface ClaimSummary {
  totalClaim?: number;
  draftClaim?: number;
  inProgressClaim?: number;
  approvedClaim?: number;
  completedClaim?: number;
  rejectedClaim?: number;
  cancelClaim?: number;
}

type ClaimStatus =
  | 'DRAFT'
  | 'IN_PROGRESS'
  | 'APPROVED'
  | 'COMPLETED'
  | 'CANCEL'
  | 'REJECTED';

interface ClaimRow {
  id: number;
  claimNumber: string;
  customerCode: string;
  indexNumber: string;
  status: ClaimStatus | string;
  statusName: string;
  category: string;
  reason: string;
  scheme: string;
  createDate: string;
  amount: string;
}

/** Raw row shape returned by `/claim/page` (`ClaimPageItemVO`). */
interface ClaimPageItemVO {
  id: number;
  claimNumber: string;
  customerCode?: string;
  customerReferenceId: string;
  claimStatus: string;
  claimStatusName: string;
  claimType: string;
  claimTypeName: string;
  claimCategory: string;
  claimCategoryName: string;
  ruleLevel2: string;
  ruleLevel2Name: string;
  claimValue: number | string;
  currency: string;
  createdTime: string;
}

interface ClaimDetailItem {
  materialNumber: string;
  invoiceAmount: string;
  orderLineQty: number;
  deliveryNumber: string;
  dnItemNo: string;
  deliveryQty: number;
  submittableQty: number;
  claimQty: number;
  claimAmount: string;
  receiverNumber: string;
  returnOrderNumber: string;
  // order mode extra fields
  orderNumber?: string;
  orderLineNumber?: string;
  orderTotalAmount?: string;
  orderQuantity?: number;
  // invoice mode extra fields
  invoiceNo?: string;
  invoiceItemNo?: string;
  dnTotalValueWithTax?: string;
  dnQuantity?: number;
  priceDifference?: string;
}

@Component({
  selector: 'claim-overview',
  templateUrl: './claim-overview.component.html',
  styleUrls: ['./claim-overview.component.less'],
})
export class ClaimOverviewComponent implements OnInit, OnDestroy {
  // Status step cards — colors taken from Figma variable defs (Bosch design tokens)
  // Per Figma: each step has its own brand-colored label + matching icon.
  //   草稿  — neutral grey (Bosch grey)
  //   进行中 — Bosch blue       #007BC0
  //   已受理 — Bosch orange     #ED8B00
  //   已完成 — Bosch turquoise  #18837E
  //   已拒绝 — Bosch red        #ED0007
  statusCards: StatusCard[] = [
    {
      key: 'all',
      label: 'CLAIM_STATUS_ALL',
      count: 0,
      icon: 'appstore',
      color: '#9E2896',
    },
    {
      key: 'draft',
      label: 'CLAIM_STATUS_DRAFT',
      count: 0,
      icon: 'form',
      color: '#71767C',
    },
    {
      key: 'inProgress',
      label: 'CLAIM_STATUS_IN_PROGRESS',
      count: 0,
      icon: 'hourglass',
      color: '#FF8726',
    },
    {
      key: 'accepted',
      label: 'CLAIM_STATUS_RECEIVED',
      count: 0,
      icon: 'import',
      color: '#009C5F',
    },
    {
      key: 'completed',
      label: 'COMPLETED',
      count: 0,
      icon: 'check-circle',
      color: '#18837E',
    },
    {
      key: 'rejected',
      label: 'CLAIM_STATUS_REJECTED',
      count: 0,
      icon: 'stop',
      color: '#ED0007',
    },
    {
      key: 'cancel',
      label: 'CANCELLED',
      count: 0,
      icon: 'close',
      color: '#007BC0',
    },
  ];

  activeCard: StatusCardKey = 'all';
  hoveredCard: StatusCardKey | null = null;

  searchForm!: FormGroup;

  ranges: Record<string, Date[]> = {};

  reasonOptions: { label: string; value: string }[] = [];

  categoryOptions: { label: string; value: string; parentName?: string }[] = [];

  claimMethodOptions: { label: string; value: string }[] = [];

  statusOptions = [
    { label: 'CLAIM_STATUS_DRAFT', value: 'DRAFT' },
    { label: 'CLAIM_STATUS_IN_PROGRESS', value: 'IN_PROGRESS' },
    { label: 'CLAIM_STATUS_RECEIVED', value: 'APPROVED' },
    { label: 'COMPLETED', value: 'COMPLETED' },
    { label: 'CLAIM_STATUS_REJECTED', value: 'REJECTED' },
    { label: 'CANCELLED', value: 'CANCEL' },
  ];

  // Column widths from Figma (frame 165:1452): 141 / 165 / 100 / 147 / 132 / 98 / 132 / 170 / 112
  // STATUS is widened to 160 so the English badge "Being Processed"
  // (≈138px incl. chip padding + 16px left cell padding) stays inside the cell.
  columns = [
    { key: 'claimNumber', name: 'CLAIM_NUMBER', width: 150 },
    { key: 'customerCode', name: 'CLAIM_CUSTOMER_NO', width: 120 },
    { key: 'status', name: 'STATUS', width: 160 },
    { key: 'reason', name: 'CLAIM_REASON', width: 130 },
    { key: 'category', name: 'CLAIM_CATEGORY', width: 130 },
    { key: 'scheme', name: 'CLAIM_SCHEME', width: 130 },
    { key: 'createDate', name: 'CLAIM_CREATE_DATE', width: 150 },
    { key: 'amount', name: 'CLAIM_NET_VALUE', width: 140 },
    { key: 'viewDetail', name: 'CLAIM_DETAILS', width: 120 },
    { key: 'action', name: 'ACTION', width: 140 },
  ];

  dataSource: ClaimRow[] = [];
  overviewLoading = false;
  metaLoading = false;
  tableLoading = false;

  pagination = {
    total: 0,
    pageIndex: 1,
    pageSize: 10,
    pageSizeOptions: [10, 20, 50],
  };

  // Claim details popup (read-only) — opened via the caret-down icon in the
  // 索赔金额 column. Mirrors the cart popup in `create-claim` but locked to view.
  detailOpen = false;
  detailClaimNumber: string | null = null;
  detailItems: ClaimDetailItem[] = [];
  detailLoading = false;
  detailIsOrderMode = false;
  detailClaimTypeName = '';
  detailClaimCategoryName = '';
  private customerSubscription?: Subscription;

  /** 非寄售 + 索赔原因=价格差异 + 索赔分类=价格差异 时显示「正确的销售单价」列 */
  get detailIsPriceDifferenceMode(): boolean {
    if (this.detailIsOrderMode) return false;
    return this.detailClaimTypeName === '价格差异' && this.detailClaimCategoryName === '价格差异';
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private message: NzMessageService,
    private claimService: ClaimService,
    private customerService: CustomerService,
    private claimPdfExportService: ClaimPdfExportService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadClaimOverview();
    this.searchForm = this.fb.group({
      dateRange: [[]],
      reason: [null],
      claimMethod: [null],
      category: [null],
      claimNumber: [''],
      amountMin: [''],
      amountMax: [''],
      deliveryNumber: [''],
      invoice: [''],
      material: [''],
      order: [''],
      indexNumber: [''],
      status: [null],
    });

    this.initRanges();
    this.refreshMetaCascade();
    this.bindMetaCascade();
    this.loadClaimSolutions();
    this.loadClaimPage();

    this.customerSubscription = this.customerService.selectedCustomer$
      .pipe(skip(1))
      .subscribe(() => {
        this.handleCustomerChange();
      });
  }

  ngOnDestroy(): void {
    this.customerSubscription?.unsubscribe();
  }

  private initRanges(): void {
    const startOfMonth = moment().startOf('month').toDate();
    const endOfMonth = moment().endOf('month').toDate();
    const startOfLastMonth = moment().subtract(1, 'month').startOf('month').toDate();
    const endOfLastMonth = moment().subtract(1, 'month').endOf('month').toDate();
    const startOfLastThreeMonths = moment().subtract(3, 'months').startOf('month').toDate();
    this.ranges = {
      'CLAIM_RANGE_THIS_MONTH': [startOfMonth, endOfMonth],
      'CLAIM_RANGE_LAST_MONTH': [startOfLastMonth, endOfLastMonth],
      'CLAIM_RANGE_LAST_3_MONTHS': [startOfLastThreeMonths, endOfMonth],
      'CLAIM_RANGE_THIS_YEAR': [moment().startOf('year').toDate(), moment().endOf('year').toDate()],
    };
  }

  /** nz-range-picker 的 [nzRanges] 需要显示文本，这里把 i18n key 实时翻译。 */
  get translatedRanges(): Record<string, Date[]> {
    return Object.fromEntries(
      Object.entries(this.ranges).map(([key, value]) => [
        this.translate.instant(key),
        value,
      ]),
    );
  }

  private handleCustomerChange(): void {
    this.pagination.pageIndex = 1;
    this.loadClaimOverview();
    this.refreshMetaCascade();
    this.loadClaimSolutions();
    this.loadClaimPage();
  }

  /**
   * Loads 索赔方式 options from the dedicated `/claimMeta/claimSolutions` endpoint.
   */
  private loadClaimSolutions(): void {
    this.claimService.apiClaimSolutions().subscribe({
      next: (result: any) => {
        const items: { value: string; name: string }[] = result?.rtnData ?? [];
        this.claimMethodOptions = items.map(item => ({
          label: item.name,
          value: item.value,
        }));
      },
      error: () => {
        this.message.error(this.translate.instant('CLAIM_MSG_SOLUTIONS_FAIL'));
      },
    });
  }

  /**
   * Loads the top 5 status-card counts from `/claim/overview` and maps the
   * backend `ClaimSummaryVO` fields onto the local `statusCards` entries.
   * Backend → card key mapping:
   *   draftClaim      → draft
   *   inProgressClaim → inProgress
   *   approvedClaim   → accepted
   *   completedClaim  → completed
   *   rejectedClaim   → rejected
   */
  private loadClaimOverview(): void {
    this.overviewLoading = true;
    this.claimService.apiClaimOverview().subscribe({
      next: (result: any) => {
        const summary: ClaimSummary = result?.rtnData ?? {};
        const counts: Record<StatusCardKey, number> = {
          all: 0,
          draft: summary.draftClaim ?? 0,
          inProgress: summary.inProgressClaim ?? 0,
          accepted: summary.approvedClaim ?? 0,
          completed: summary.completedClaim ?? 0,
          rejected: summary.rejectedClaim ?? 0,
          cancel: summary.cancelClaim ?? 0,
        };
        counts.all = counts.draft + counts.inProgress + counts.accepted +
          counts.completed + counts.rejected + counts.cancel;
        this.statusCards = this.statusCards.map(card => ({
          ...card,
          count: counts[card.key] ?? 0,
        }));
        this.overviewLoading = false;
      },
      error: () => {
        this.overviewLoading = false;
        this.message.error(this.translate.instant('CLAIM_MSG_OVERVIEW_FAIL'));
      },
    });
  }

  /**
   * 索赔原因 / 索赔方式 / 索赔分类 三个下拉的级联联动。
   *
   * 后端 `/claimMeta/cascade` 一次性返回 5 类 meta 的可选项，按照"单向收敛"策略：
   *   - claimTypes      —— 始终全集
   *   - claimCategories —— 受 claimTypeName 收敛
   *   - claimSolutions  —— 受前 4 项已选条件收敛
   *
   * 因此只要任一下拉变化，就用三者当前值作为入参重拉一次，再用结果刷新另外两个
   * 下拉的选项；若已选值不在新选项中则清空。
   */
  private bindMetaCascade(): void {
    (['reason', 'claimMethod', 'category'] as const).forEach(controlName => {
      this.searchForm
        .get(controlName)!
        .valueChanges.subscribe(() => this.refreshMetaCascade());
    });
  }

  private refreshMetaCascade(): void {
    const reason = this.searchForm?.get('reason')?.value ?? null;
    const category = this.searchForm?.get('category')?.value ?? null;
    const claimMethod = this.searchForm?.get('claimMethod')?.value ?? null;

    this.metaLoading = true;
    this.claimService
      .apiClaimMetaCascade({
        claimTypeName: reason,
        claimCategoryName: category,
        claimSolutionName: claimMethod,
      })
      .subscribe({
        next: (result: any) => {
          const data = result?.rtnData ?? {};
          const claimTypes: { value: string; name: string }[] =
            data.claimTypes ?? [];
          const claimCategories: {
            value: string;
            name: string;
            parentName?: string;
          }[] = data.claimCategories ?? [];
          const claimSolutions: { value: string; name: string }[] =
            data.claimSolutions ?? [];

          this.reasonOptions = claimTypes.map(item => ({
            label: item.name,
            value: item.value,
          }));
          this.categoryOptions = claimCategories.map(item => ({
            label: item.name,
            value: item.value,
            parentName: item.parentName,
          }));

          this.clearStaleSelection('reason', this.reasonOptions);
          this.clearStaleSelection('category', this.categoryOptions);
          this.metaLoading = false;
        },
        error: () => {
          this.metaLoading = false;
          this.message.error(this.translate.instant('CLAIM_MSG_META_FAIL'));
        },
      });
  }

  /**
   * If the current value of `controlName` is no longer present in `options`
   * (because the cascade narrowed the list), clear it silently so the user
   * isn't left with an invalid selection. `emitEvent: false` prevents an
   * infinite cascade-refresh loop.
   */
  private clearStaleSelection(
    controlName: 'reason' | 'category' | 'claimMethod',
    options: { value: string }[],
  ): void {
    const control = this.searchForm.get(controlName);
    const value = control?.value;
    if (value && !options.some(opt => opt.value === value)) {
      control!.setValue(null, { emitEvent: false });
    }
  }

  // Status chip colors per Figma variable defs (mapped to backend status enum):
  //   DRAFT       — 灰
  //   IN_PROGRESS — 紫
  //   APPROVED    — 橙
  //   COMPLETED   — 蓝绿
  //   REJECTED    — 红
  getStatusLabel(status: string): string {
    switch (status) {
      case 'DRAFT':
        return 'CLAIM_STATUS_DRAFT';
      case 'IN_PROGRESS':
        return 'CLAIM_STATUS_IN_PROGRESS';
      case 'APPROVED':
        return 'CLAIM_STATUS_RECEIVED';
      case 'COMPLETED':
        return 'COMPLETED';
      case 'REJECTED':
        return 'CLAIM_STATUS_REJECTED';
      case 'CANCEL':
        return 'CANCELLED';
      default:
        return status;
    }
  }

  getStatusColor(status: string): { color: string; bg: string } {
    switch (status) {
      case 'DRAFT':
        return { color: '#71767C', bg: '#F2F3F4' };
      case 'IN_PROGRESS':
        return { color: '#FF8726', bg: '#FFF6EE' };
      case 'APPROVED':
        return { color: '#009C5F', bg: '#E6F6EF' };
      case 'COMPLETED':
        return { color: '#18837E', bg: '#F1F8F7' };
      case 'REJECTED':
        return { color: '#ED0007', bg: '#FEEDED' };
      case 'CANCEL':
        return { color: '#007BC0', bg: '#E6F2F9' };
      default:
        return { color: '#A0A0A0', bg: '#F2F3F4' };
    }
  }

  onStatusCardClick(cardKey: StatusCardKey): void {
    this.activeCard = cardKey;
    if (cardKey === 'all') {
      this.searchForm.get('status')?.setValue(null);
    } else {
      const statusMap: Record<string, string> = {
        draft: 'DRAFT',
        inProgress: 'IN_PROGRESS',
        accepted: 'APPROVED',
        completed: 'COMPLETED',
        rejected: 'REJECTED',
        cancel: 'CANCEL',
      };
      this.searchForm.get('status')?.setValue(statusMap[cardKey] ?? null);
    }
    this.pagination.pageIndex = 1;
    this.loadClaimPage();
  }

  hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  handleSearch(): void {
    this.pagination.pageIndex = 1;
    this.loadClaimPage();
  }

  /**
   * Loads one page of `/claim/page`. Uses the current filter form snapshot
   * (mapped via `buildClaimQuery`) plus pagination state, then converts the
   * backend `ClaimPageItemVO` rows into the local `ClaimRow` shape consumed
   * by the table template.
   */
  private loadClaimPage(): void {
    const req = {
      param: this.buildClaimQuery(),
      current: this.pagination.pageIndex,
      size: this.pagination.pageSize,
    };
    this.tableLoading = true;
    this.claimService.apiClaimPage(req).subscribe({
      next: (result: any) => {
        const data = result?.rtnData ?? {};
        const rows: ClaimPageItemVO[] = data.rows ?? [];
        const total: number = data.total ?? 0;
        this.dataSource = rows.map(vo => this.mapClaimRow(vo));
        this.pagination.total = total;
        this.tableLoading = false;
      },
      error: () => {
        this.tableLoading = false;
        this.message.error(this.translate.instant('CLAIM_MSG_LIST_FAIL'));
      },
    });
  }

  private mapClaimRow(vo: ClaimPageItemVO): ClaimRow {
    return {
      id: vo.id,
      claimNumber: vo.claimNumber ?? '',
      customerCode: vo.customerCode ?? '',
      indexNumber: vo.customerReferenceId ?? '',
      status: vo.claimStatus ?? '',
      statusName: vo.claimStatusName ?? '',
      category: vo.claimCategoryName ?? vo.claimCategory ?? '',
      reason: vo.claimTypeName ?? vo.claimType ?? '',
      scheme: vo.ruleLevel2Name ?? vo.ruleLevel2 ?? '',
      createDate: this.formatDate(vo.createdTime),
      amount: this.formatAmount(vo.claimValue, vo.currency),
    };
  }

  /** `2026-05-12 10:00:00` → `2026/05/12`. */
  private formatDate(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return value.split(' ')[0].replace(/-/g, '/');
  }

  private formatAmount(
    value: number | string | null | undefined,
    currency: string | null | undefined,
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) {
      return `${currency ?? ''} ${value}`.trim();
    }
    const formatted = num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${currency ?? ''} ${formatted}`.trim();
  }

  exportLoading = false;

  /**
   * Maps the current filter form into the backend's `ClaimQueryReqVO` shape.
   * Empty / null fields are dropped so the backend doesn't apply useless
   * filters. Used by both list query (future) and Excel export.
   */
  private buildClaimQuery(): Record<string, any> {
    const v = this.searchForm.value;
    const formatDate = (d: Date | string | null): string | null => {
      if (!d) return null;
      if (typeof d === 'string') return d;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const raw: Record<string, any> = {
      claimNumber: v.claimNumber,
      customerReferenceId: v.indexNumber,
      claimStartDate: v.dateRange?.[0] ? formatDate(v.dateRange[0]) : null,
      claimEndDate: v.dateRange?.[1] ? formatDate(v.dateRange[1]) : null,
      claimType: v.reason,
      claimCategory: v.category,
      claimSolution: v.claimMethod,
      claimStatus: v.status,
      claimAmountMin: v.amountMin,
      claimAmountMax: v.amountMax,
      dnNo: v.deliveryNumber,
      invoiceNo: v.invoice,
      materialCode: v.material,
      orderNo: v.order,
    };
    return Object.fromEntries(
      Object.entries(raw).filter(
        ([, val]) => val !== null && val !== undefined && val !== '',
      ),
    );
  }

  handleExport(): void {
    if (this.exportLoading) {
      return;
    }
    this.exportLoading = true;

    const query = this.buildClaimQuery();
    this.claimService
      .apiClaimExport(query)
      .pipe(finalize(() => (this.exportLoading = false)))
      .subscribe({
        next: async response => {
          const blob = response.body;
          if (!blob || blob.size === 0) {
            this.message.warning(this.translate.instant('CLAIM_MSG_NO_DATA_EXPORT'));
            return;
          }

          // 当鉴权失败 / 业务异常时，后端会以 application/json 返回标准
          // ResponseData，但因为我们声明了 responseType: 'blob'，这里仍然会
          // 拿到 Blob。直接 saveAs 会得到一个内容是 JSON、扩展名是 .xlsx 的
          // 坏文件，Excel 打开时会报 "file format or file extension is not valid"。
          const contentType = (
            response.headers.get('Content-Type') || ''
          ).toLowerCase();
          if (
            contentType.includes('application/json') ||
            contentType.includes('text/')
          ) {
            try {
              const text = await blob.text();
              const payload = text ? JSON.parse(text) : null;
              const msg =
                payload?.rtnMsg ||
                payload?.message ||
                this.translate.instant('CLAIM_MSG_EXPORT_FAIL');
              this.message.error(msg);
            } catch {
              this.message.error(this.translate.instant('CLAIM_MSG_EXPORT_FAIL'));
            }
            return;
          }

          saveAs(
            blob,
            this.resolveExportFileName(
              response.headers.get('Content-Disposition'),
            ),
          );
        },
        error: () => {
          this.message.error(this.translate.instant('CLAIM_MSG_EXPORT_FAIL'));
        },
      });
  }

  private resolveExportFileName(contentDisposition: string | null): string {
    const fallback = `claim_${new Date()
      .toISOString()
      .slice(0, 10)
      .replace(/-/g, '')}.xlsx`;
    if (!contentDisposition) {
      return fallback;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
      try {
        return decodeURIComponent(utf8Match[1].trim());
      } catch {
        // ignore and try the next pattern
      }
    }

    const plainMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (plainMatch && plainMatch[1]) {
      try {
        return decodeURIComponent(plainMatch[1].trim());
      } catch {
        return plainMatch[1].trim();
      }
    }

    return fallback;
  }

  handleClear(): void {
    this.searchForm.reset({
      dateRange: [],
      reason: null,
      claimMethod: null,
      category: null,
      claimNumber: '',
      amountMin: '',
      amountMax: '',
      deliveryNumber: '',
      invoice: '',
      material: '',
      order: '',
      indexNumber: '',
      status: null,
    });
    this.pagination.pageIndex = 1;
    this.loadClaimPage();
  }

  /**
   * 金额输入框限制：只允许正数（数字和小数点，不允许负号和其他字符）。
   */
  sanitizeAmountInput(controlName: 'amountMin' | 'amountMax', event: Event): void {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(/[^\d.]/g, '');
    // 只保留第一个小数点
    const parts = cleaned.split('.');
    const sanitized = parts.length > 1
      ? parts[0] + '.' + parts.slice(1).join('')
      : parts[0];
    if (input.value !== sanitized) {
      input.value = sanitized;
      this.searchForm.get(controlName)?.setValue(sanitized, { emitEvent: false });
    }
  }

  handleCreate(): void {
    const country =
      this.route.snapshot.paramMap.get('country') ||
      localStorage.getItem('country') ||
      'iam';
    this.router.navigate([country, 'claim', 'create']);
  }

  onClaimNumberClick(row: ClaimRow): void {
    if (!row.id) {
      this.message.warning(this.translate.instant('CLAIM_MSG_NO_ID_DETAIL'));
      return;
    }
    const country =
      this.route.snapshot.paramMap.get('country') ||
      localStorage.getItem('country') ||
      'iam';
    if (row.status === 'DRAFT') {
      // 草稿状态：点击索赔单号 = 编辑
      this.router.navigate([country, 'claim', 'edit', row.id], {
        state: { claim: row },
      });
    } else {
      // 已提交：跳转到 detail 页（只读）
      this.router.navigate([country, 'claim', 'detail', row.id], {
        state: { claimNumber: row.claimNumber },
      });
    }
  }

  onEdit(row: ClaimRow): void {
    if (!row.id) {
      this.message.warning(this.translate.instant('CLAIM_MSG_NO_ID_EDIT'));
      return;
    }
    const country =
      this.route.snapshot.paramMap.get('country') ||
      localStorage.getItem('country') ||
      'iam';
    this.router.navigate([country, 'claim', 'edit', row.id], {
      state: { claim: row },
    });
  }

  onCopy(row: ClaimRow): void {
    if (!row.id) {
      this.message.warning(this.translate.instant('CLAIM_MSG_NO_ID_COPY'));
      return;
    }
    if (this.copyLoading.get(row.id)) {
      return;
    }

    this.copyLoading.set(row.id, true);
    this.claimService
      .apiClaimCopy(row.id)
      .pipe(finalize(() => this.copyLoading.set(row.id, false)))
      .subscribe({
        next: () => {
          this.message.success(this.translate.instant('CLAIM_MSG_COPY_SUCCESS'));
          this.loadClaimPage();
          this.loadClaimOverview();
        },
        error: () => {
          this.message.error(this.translate.instant('CLAIM_MSG_COPY_FAIL'));
        },
      });
  }

  /**
   * Deletes a Claim draft via `DELETE /claim/draft/{id}`. Backend rejects
   * non-DRAFT rows, so the trash icon is also conditionally hidden in the
   * template — this is a defensive guard in case the row was mutated.
   */
  onDelete(row: ClaimRow): void {
    if (row.status !== 'DRAFT' || !row.id) {
      return;
    }
    this.claimService.apiDeleteClaimDraft(row.id).subscribe({
      next: () => {
        this.message.success(this.translate.instant('CLAIM_MSG_DRAFT_DELETED'));
        // 删除后如果当前页只剩一条且不是第一页，回退一页避免空页。
        if (
          this.dataSource.length === 1 &&
          this.pagination.pageIndex > 1
        ) {
          this.pagination.pageIndex -= 1;
        }
        this.loadClaimPage();
        this.loadClaimOverview();
      },
      error: () => {
        this.message.error(this.translate.instant('CLAIM_MSG_DRAFT_DELETE_FAIL'));
      },
    });
  }

  /**
   * Opens the read-only claim details popup by loading `/claim/{id}` and
   * rendering `references[].items`.
   */
  openClaimDetails(row: ClaimRow): void {
    this.detailClaimNumber = row.claimNumber;
    this.detailOpen = true;
    this.detailItems = [];
    this.detailLoading = false;
    this.detailIsOrderMode = false;

    if (!row.id) {
      this.message.warning(this.translate.instant('CLAIM_MSG_NO_ID_ITEMDETAIL'));
      return;
    }

    this.detailLoading = true;
    this.claimService.apiClaimDetail(row.id).subscribe({
      next: (result: any) => {
        const vo = result?.rtnData ?? {};
        this.detailClaimNumber = vo.claimNumber ?? row.claimNumber;
        const ruleLevel1 = (vo.ruleLevel1 ?? '').toString().toLowerCase();
        this.detailIsOrderMode = ruleLevel1 === 'consignment';
        this.detailClaimTypeName = vo.claimTypeName ?? '';
        this.detailClaimCategoryName = vo.claimCategoryName ?? '';
        this.detailItems = this.mapClaimDetailItemsFromReferences(
          vo.references ?? [],
        );
        this.detailLoading = false;
      },
      error: () => {
        this.detailItems = [];
        this.detailLoading = false;
        this.message.error(this.translate.instant('CLAIM_MSG_DETAIL_FAIL'));
      },
    });
  }

  closeClaimDetails(): void {
    this.detailOpen = false;
    this.detailLoading = false;
  }

  private mapClaimDetailItemsFromReferences(references: any[]): ClaimDetailItem[] {
    const rows: ClaimDetailItem[] = [];
    references.forEach(ref => {
      const items = Array.isArray(ref?.items) ? ref.items : [];
      items.forEach((item: any) => rows.push(this.mapReferenceItemToDetail(item, ref)));
    });
    return rows;
  }

  private mapReferenceItemToDetail(item: any, ref: any): ClaimDetailItem {
    const qty = Number(item?.claimQuantity ?? 0);
    const unitPrice = Number(item?.unitPriceWithTax ?? item?.unitPrice ?? 0);
    const claimAmountCalc = unitPrice > 0 ? qty * unitPrice : Number(item?.dnTotalValueWithTax ?? 0);
    const referenceNo = ref?.referenceNo ?? '';
    const isInvoice = (ref?.referenceType ?? '').toString().toUpperCase() === 'INVOICE';
    const isOrderMode = !isInvoice;
    return {
      materialNumber: item?.materialCode ?? '',
      invoiceAmount: this.formatAmount(
        item?.dnTotalValueWithTax ?? item?.unitPriceWithTax ?? item?.unitPrice ?? 0,
        item?.currency ?? 'CNY',
      ),
      orderLineQty: Number(item?.dnQuantity ?? 0),
      deliveryNumber: item?.dnNo ?? '',
      dnItemNo: isOrderMode
        ? (item?.dnItemNo ?? '')
        : (item?.referenceItemNo ?? item?.dnItemNo ?? ''),
      deliveryQty: Number(item?.dnQuantity ?? 0),
      submittableQty: Number(item?.availableQuantity ?? 0),
      claimQty: qty,
      // 优先展示后端 claimAmount，且始终格式化（CNY保留两位小数）
      claimAmount:
        item?.claimAmount != null && item?.claimAmount !== ''
          ? this.formatAmount(item.claimAmount, item?.currency ?? 'CNY')
          : this.formatAmount(claimAmountCalc, item?.currency ?? 'CNY'),
      receiverNumber: item?.receiverNumber ?? '-',
      returnOrderNumber: isInvoice ? '-' : item?.returnOrderNumber ?? '-',
      orderNumber: isOrderMode ? (item?.orderNo ?? '') : undefined,
      orderLineNumber: isOrderMode ? (item?.orderItemNo ?? '') : undefined,
      orderTotalAmount: isOrderMode
        ? this.formatAmount(Number(item?.dnTotalValueWithTax ?? 0), item?.currency ?? 'CNY')
        : undefined,
      orderQuantity: isOrderMode ? Number(item?.orderQuantity ?? item?.dnQuantity ?? 0) : undefined,
      invoiceNo: isInvoice ? (item?.invoiceNo ?? '') : undefined,
      invoiceItemNo: isInvoice ? (item?.invoiceItemNo ?? '') : undefined,
      dnTotalValueWithTax: isInvoice
        ? this.formatAmount(Number(item?.netValue ?? item?.dnTotalValueWithTax ?? 0), item?.currency ?? 'CNY')
        : undefined,
      dnQuantity: isInvoice ? Number(item?.billedQty ?? item?.invoicedQuantity ?? item?.dnQuantity ?? 0) : undefined,
      priceDifference: item?.priceDifference != null && String(item.priceDifference).trim() !== ''
        ? (String(item.priceDifference).includes('CNY')
          ? String(item.priceDifference)
          : this.formatAmount(Number(item.priceDifference), item?.currency ?? 'CNY'))
        : undefined,
    };
  }

  pageIndexChange(pageIndex: number): void {
    if (pageIndex === this.pagination.pageIndex) {
      return;
    }
    this.pagination.pageIndex = pageIndex;
    this.loadClaimPage();
  }

  pageSizeChange(pageSize: number): void {
    if (pageSize === this.pagination.pageSize) {
      return;
    }
    this.pagination.pageSize = pageSize;
    this.pagination.pageIndex = 1;
    this.loadClaimPage();
  }

  // ---- PDF Export ----

  copyLoading = new Map<number, boolean>();
  pdfExportLoading = new Map<number, boolean>();

  async exportRowAsPdf(row: ClaimRow): Promise<void> {
    if (!row.id || row.status === 'DRAFT') return;
    if (this.pdfExportLoading.get(row.id)) return;

    this.pdfExportLoading.set(row.id, true);
    try {
      const result: any = await firstValueFrom(this.claimService.apiClaimDetail(row.id));
      const vo = result?.rtnData;
      if (!vo) {
        this.message.error(this.translate.instant('CLAIM_MSG_DETAIL_FAIL'));
        return;
      }

      const fileName = `${row.claimNumber}_${row.customerCode}.pdf`.replace(/[/\\:*?"<>|]/g, '-');
      await this.claimPdfExportService.exportClaimDetail(vo, fileName);
    } catch (e) {
      console.error('PDF export error:', e);
      this.message.error(this.translate.instant('CLAIM_MSG_PDF_EXPORT_FAIL'));
    } finally {
      this.pdfExportLoading.set(row.id, false);
    }
  }
}

